import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Check, AlertCircle, ChevronLeft, Sparkles } from "lucide-react";
import { API, useRoleHome } from "@/contexts/AuthContext";
import Header from "@/components/shared/Header";

// Helper function to extract error messages
const getErrorMessage = (err, defaultMsg = "An error occurred") => {
  if (err?.response?.data?.detail) {
    const detail = err.response.data.detail;
    if (Array.isArray(detail)) {
      return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(", ");
    }
    return detail;
  }
  return err?.message || defaultMsg;
};

const SubmitInspectionPage = () => {
  const { id } = useParams();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [signature, setSignature] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [showCheckoutReminder] = useState(false); // Kept for potential future use
  const [checkoutItems, setCheckoutItems] = useState({
    fingerprints: false,
    carKit: false,
    parked: false,
    toolsRemoved: false,
    coversReplaced: false,
    itemsRemoved: false
  });
  const [customerCareStatuses, setCustomerCareStatuses] = useState({});  // item_id -> status
  const canvasRef = useRef(null);
  const navigate = useNavigate();
  // iter193 — role-aware home so post-submit "Back to Jobs" lands on
  // the right dashboard.
  const homeHref = useRoleHome();

  // Checkout reminder items
  const CHECKOUT_CHECKLIST = [
    { id: 'fingerprints', label: 'Vehicle is free of fingerprints and smudges' },
    { id: 'carKit', label: 'Vehicle car kit left tidy (warning triangle, hi-vis, first aid etc.)' },
    { id: 'parked', label: 'Vehicle reversed into a parking space accessible for the customer' },
    { id: 'toolsRemoved', label: 'All tools removed from vehicle' },
    { id: 'coversReplaced', label: 'All protective covers replaced (seat cover, floor mat, steering wheel cover)' },
    { id: 'itemsRemoved', label: 'All items used in repair/inspection removed from vehicle' }
  ];

  const allCheckoutItemsConfirmed = Object.values(checkoutItems).every(v => v);

  useEffect(() => {
    axios.get(`${API}/jobs/${id}/inspection`).then(res => {
      if (!res.data) {
        navigate(`/app/tech/job/${id}/start`);
        setLoading(false);
        return;
      }
      if (res.data.status === "submitted") {
        setSubmitted(true);
        setLoading(false);
        return;
      }
      axios.get(`${API}/inspections/${res.data.id}/summary`).then(summaryRes => {
        setSummary(summaryRes.data);
      }).finally(() => setLoading(false));
    }).catch(() => {
      setLoading(false);
    });
  }, [id, navigate]);

  // Signature pad handlers
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Scale coordinates to account for CSS scaling
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Scale coordinates to account for CSS scaling
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clientX = e.clientX || e.touches?.[0]?.clientX;
    const clientY = e.clientY || e.touches?.[0]?.clientY;
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1e293b';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      if (canvas) {
        setSignature(canvas.toDataURL('image/png'));
      }
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignature(null);
    }
  };

  const handleFinalSubmit = async () => {
    if (!allCheckoutItemsConfirmed) {
      setError("Please confirm all customer care checkout items before submitting");
      return;
    }
    if (!signature) {
      setError("Please provide your signature");
      return;
    }
    if (!confirmed) {
      setError("Please confirm the inspection details");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/inspections/${summary.inspection.id}/submit`, { 
        confirmed: true,
        technician_signature: signature,
        checkout_confirmed: true,
        customer_care_statuses: customerCareStatuses
      });
      setSubmitted(true);
    } catch (err) {
      setError(getErrorMessage(err, "Failed to submit"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    // Navigate back to the inspection sections page
    navigate(`/app/tech/job/${id}/sections`);
  };

  if (loading) return <div className="p-8 text-center">Loading...</div>;
  
  // Still loading summary data
  if (!summary && !submitted) {
    return <div className="p-8 text-center">Loading inspection summary...</div>;
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="p-4 max-w-md mx-auto text-center">
          <Card>
            <CardContent className="p-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2" data-testid="submit-success-title">Inspection Submitted!</h2>
              <p className="text-gray-600 mb-6">The inspection has been successfully submitted and cannot be edited.</p>
              <Button className="w-full" onClick={() => navigate(homeHref)} data-testid="back-to-jobs-after-submit">
                Back to Jobs
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!summary?.can_submit) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="p-4 max-w-md mx-auto text-center">
          <Card>
            <CardContent className="p-6">
              <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold">Incomplete Inspection</h2>
              <p className="text-gray-600 mt-2">
                {summary?.completed_items}/{summary?.total_items} items completed
              </p>
              <Button className="mt-4" onClick={() => navigate(`/app/tech/job/${id}/sections`)} data-testid="complete-sections-btn">
                Complete Remaining Items
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <div className="p-4 max-w-2xl mx-auto">
        {/* Back button */}
        <Button 
          variant="ghost" 
          onClick={handleBack}
          className="mb-4 text-slate-600 hover:text-slate-800"
          data-testid="back-to-inspection-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Inspection
        </Button>
        
        <h2 className="text-xl font-semibold mb-4" data-testid="submit-page-title">Review & Submit</h2>
        
        <Card className="mb-4">
          <CardHeader>
            <CardTitle>Vehicle Details</CardTitle>
          </CardHeader>
          <CardContent>
            <p><strong>Registration:</strong> {summary?.job?.reg}</p>
            <p><strong>Vehicle:</strong> {summary?.job?.make} {summary?.job?.model}</p>
            <p><strong>Mileage at Inspection:</strong> {summary?.inspection?.mileage?.toLocaleString() || 'Not recorded'} miles</p>
          </CardContent>
        </Card>

        <Card className="mb-4">
          <CardHeader>
            <CardTitle>Inspection Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-5 gap-2 text-center">
              <div className="p-2 bg-black rounded">
                <div className="text-2xl font-bold text-white">{summary?.status_counts?.dangerous || 0}</div>
                <div className="text-xs text-gray-300">Dangerous</div>
              </div>
              <div className="p-2 bg-red-100 rounded">
                <div className="text-2xl font-bold text-red-700">{summary?.status_counts?.red || 0}</div>
                <div className="text-xs text-red-600">Fail</div>
              </div>
              <div className="p-2 bg-amber-100 rounded">
                <div className="text-2xl font-bold text-amber-700">{summary?.status_counts?.amber || 0}</div>
                <div className="text-xs text-amber-600">Advisory</div>
              </div>
              <div className="p-2 bg-green-100 rounded">
                <div className="text-2xl font-bold text-green-700">{summary?.status_counts?.green || 0}</div>
                <div className="text-xs text-green-600">Pass</div>
              </div>
              <div className="p-2 bg-blue-100 rounded">
                <div className="text-2xl font-bold text-blue-700">{summary?.status_counts?.blue || 0}</div>
                <div className="text-xs text-blue-600">N/A</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Health Indicators - Battery, Brake Fluid, Coolant, Tyres, Brake Pads */}
        {summary?.health_indicators && summary.health_indicators.length > 0 && (
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="text-blue-600">⚡</span> Vehicle Health Check
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-500 mb-4">Key safety and wear components</p>
              
              {/* Fluids & Battery - Circular indicators */}
              {summary.health_indicators.filter(i => !i.indicator_type).length > 0 && (
                <div className="mb-6">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Fluids & Battery</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {summary.health_indicators.filter(i => !i.indicator_type).map((indicator, idx) => {
                      // Determine the proper label based on type
                      const displayLabel = indicator.is_brake_fluid ? 'Brake Fluid Health' : 
                                          indicator.is_battery ? 'Battery Health' :
                                          indicator.is_coolant ? 'Coolant Health' :
                                          indicator.label;
                      const showDegradation = indicator.is_brake_fluid || indicator.is_battery || indicator.is_coolant;
                      const degradationPct = indicator.degradation_percentage || (100 - indicator.health_percentage);
                      
                      return (
                        <div 
                          key={idx}
                          className={`rounded-lg p-3 border ${
                            indicator.health_color === 'green' ? 'bg-emerald-50 border-emerald-200' :
                            indicator.health_color === 'amber' ? 'bg-amber-50 border-amber-200' :
                            indicator.health_color === 'red' ? 'bg-red-50 border-red-200' :
                            'bg-slate-50 border-slate-200'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="relative w-12 h-12 flex-shrink-0" title={`Health: ${indicator.health_percentage}% | Degradation: ${degradationPct}%`}>
                              <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 36 36">
                                <circle className="text-slate-200" strokeWidth="3" stroke="currentColor" fill="transparent" r="14" cx="18" cy="18" />
                                <circle
                                  className={indicator.health_color === 'green' ? 'text-emerald-500' : indicator.health_color === 'amber' ? 'text-amber-500' : indicator.health_color === 'red' ? 'text-red-500' : 'text-slate-400'}
                                  strokeWidth="3" strokeLinecap="round" stroke="currentColor" fill="transparent" r="14" cx="18" cy="18"
                                  strokeDasharray={`${(indicator.health_percentage || 0) * 0.88} 100`}
                                />
                              </svg>
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className={`text-xs font-bold ${indicator.health_color === 'green' ? 'text-emerald-700' : indicator.health_color === 'amber' ? 'text-amber-700' : indicator.health_color === 'red' ? 'text-red-700' : 'text-slate-600'}`}>
                                  {indicator.health_percentage}%
                                </span>
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-slate-700">
                                {displayLabel}
                              </div>
                              <div className={`text-xs font-semibold ${indicator.health_color === 'green' ? 'text-emerald-600' : indicator.health_color === 'amber' ? 'text-amber-600' : indicator.health_color === 'red' ? 'text-red-600' : 'text-slate-500'}`}>
                                {indicator.health_label}
                              </div>
                              <div className="text-xs text-slate-400">
                                {indicator.measurement_value}{indicator.measurement_unit}
                                {showDegradation && (
                                  <span className="ml-1 text-slate-500">
                                    ({degradationPct}% degraded)
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          {showDegradation && (
                            <p className="text-xs text-slate-400 italic mt-2 pt-2 border-t border-slate-200/50">
                              Health % = serviceability. Degradation % = 100 − Health %.
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Tyre Indicators - Horizontal Bars */}
              {summary.health_indicators.filter(i => i.indicator_type === 'tyre').length > 0 && (
                <div className="mb-6">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Tyre Tread Depth</p>
                  <div className="grid grid-cols-2 gap-3">
                    {summary.health_indicators.filter(i => i.indicator_type === 'tyre').map((indicator, idx) => (
                      <div key={idx} className="bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-slate-700">{indicator.label}</span>
                          <span className={`text-sm font-bold ${indicator.health_color === 'green' ? 'text-emerald-600' : indicator.health_color === 'amber' ? 'text-amber-600' : 'text-red-600'}`}>
                            {indicator.measurement_value}mm
                          </span>
                        </div>
                        <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${indicator.health_color === 'green' ? 'bg-emerald-500' : indicator.health_color === 'amber' ? 'bg-amber-500' : 'bg-red-500'}`}
                            style={{ width: `${indicator.health_percentage}%` }}
                          />
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <span className={`text-xs ${indicator.health_color === 'green' ? 'text-emerald-600' : indicator.health_color === 'amber' ? 'text-amber-600' : 'text-red-600'}`}>
                            {indicator.health_label}
                          </span>
                          <span className="text-xs text-slate-400">{indicator.health_percentage}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Brake Pad Indicators - Horizontal Bars */}
              {summary.health_indicators.filter(i => i.indicator_type === 'brake_pads').length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Brake Pad Thickness</p>
                  <div className="grid grid-cols-2 gap-3">
                    {summary.health_indicators.filter(i => i.indicator_type === 'brake_pads').map((indicator, idx) => (
                      <div key={idx} className="bg-slate-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-slate-700">{indicator.label}</span>
                          <span className={`text-sm font-bold ${indicator.health_color === 'green' ? 'text-emerald-600' : indicator.health_color === 'amber' ? 'text-amber-600' : 'text-red-600'}`}>
                            {indicator.measurement_value}mm
                          </span>
                        </div>
                        <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${indicator.health_color === 'green' ? 'bg-emerald-500' : indicator.health_color === 'amber' ? 'bg-amber-500' : 'bg-red-500'}`}
                            style={{ width: `${indicator.health_percentage}%` }}
                          />
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <span className={`text-xs ${indicator.health_color === 'green' ? 'text-emerald-600' : indicator.health_color === 'amber' ? 'text-amber-600' : 'text-red-600'}`}>
                            {indicator.health_label}
                          </span>
                          <span className="text-xs text-slate-400">{indicator.health_percentage}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Customer Care Checkout Section - MUST complete before signing */}
        {/* Pulsing red background when incomplete to prevent techs skimming past */}
        <style>
          {`
            @keyframes pulse-red {
              0%, 100% { background-color: rgb(254, 202, 202); }
              50% { background-color: rgb(252, 165, 165); }
            }
            .pulse-red-bg {
              animation: pulse-red 1.5s ease-in-out infinite;
            }
          `}
        </style>
        <Card className={`mb-4 ${allCheckoutItemsConfirmed ? 'border-green-300' : 'border-red-400 border-2'}`}>
          <CardHeader className={`border-b ${allCheckoutItemsConfirmed ? 'bg-green-50 border-green-200' : 'pulse-red-bg border-red-300'}`}>
            <CardTitle className={`flex items-center gap-2 ${allCheckoutItemsConfirmed ? 'text-green-800' : 'text-red-800'}`}>
              <Sparkles className={`w-5 h-5 ${allCheckoutItemsConfirmed ? 'text-green-500' : 'text-red-600'}`} />
              Customer Care Checkout
              {!allCheckoutItemsConfirmed && <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded-full ml-2 font-normal">ACTION REQUIRED</span>}
            </CardTitle>
            <p className={`text-sm mt-1 ${allCheckoutItemsConfirmed ? 'text-green-700' : 'text-red-700 font-medium'}`}>
              {allCheckoutItemsConfirmed 
                ? "Excellent! All customer care items confirmed."
                : "⚠️ Don't let down your excellent work by poor customer care. Please confirm ALL items before signing."}
            </p>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-3">
              {CHECKOUT_CHECKLIST.map((item) => {
                const careStatus = customerCareStatuses[item.id] || "";
                const isCompleted = careStatus === "completed";
                const isNotCompleted = careStatus === "not_completed_further_work";
                const isNA = careStatus === "not_applicable";
                const hasStatus = !!careStatus;
                
                return (
                  <div 
                    key={item.id} 
                    className={`p-3 rounded-lg border transition-colors ${
                      isCompleted ? 'bg-green-50 border-green-200' 
                      : isNotCompleted ? 'bg-amber-50 border-amber-200'
                      : isNA ? 'bg-slate-50 border-slate-200'
                      : 'bg-white border-slate-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id={`checkout-${item.id}`}
                        checked={checkoutItems[item.id]}
                        onCheckedChange={(checked) => {
                          setCheckoutItems(prev => ({ ...prev, [item.id]: checked }));
                          if (checked && !careStatus) {
                            setCustomerCareStatuses(prev => ({ ...prev, [item.id]: "completed" }));
                          }
                        }}
                        data-testid={`checkout-${item.id}`}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <label 
                          htmlFor={`checkout-${item.id}`} 
                          className={`text-sm cursor-pointer block ${
                            isCompleted ? 'text-green-700' : isNotCompleted ? 'text-amber-700' : 'text-slate-700'
                          }`}
                        >
                          {item.label}
                        </label>
                        <div className="flex gap-2 mt-2">
                          <button
                            type="button"
                            className={`text-xs px-2 py-1 rounded border transition-colors ${
                              isCompleted ? 'bg-green-600 text-white border-green-600' : 'border-gray-300 hover:border-green-400 text-gray-600'
                            }`}
                            onClick={() => {
                              setCustomerCareStatuses(prev => ({ ...prev, [item.id]: "completed" }));
                              setCheckoutItems(prev => ({ ...prev, [item.id]: true }));
                            }}
                            data-testid={`care-completed-${item.id}`}
                          >
                            Completed
                          </button>
                          <button
                            type="button"
                            className={`text-xs px-2 py-1 rounded border transition-colors ${
                              isNotCompleted ? 'bg-amber-600 text-white border-amber-600' : 'border-gray-300 hover:border-amber-400 text-gray-600'
                            }`}
                            onClick={() => {
                              setCustomerCareStatuses(prev => ({ ...prev, [item.id]: "not_completed_further_work" }));
                              setCheckoutItems(prev => ({ ...prev, [item.id]: true }));
                            }}
                            data-testid={`care-not-completed-${item.id}`}
                          >
                            Not Completed - Further Work
                          </button>
                          <button
                            type="button"
                            className={`text-xs px-2 py-1 rounded border transition-colors ${
                              isNA ? 'bg-slate-500 text-white border-slate-500' : 'border-gray-300 hover:border-slate-400 text-gray-600'
                            }`}
                            onClick={() => {
                              setCustomerCareStatuses(prev => ({ ...prev, [item.id]: "not_applicable" }));
                              setCheckoutItems(prev => ({ ...prev, [item.id]: true }));
                            }}
                            data-testid={`care-na-${item.id}`}
                          >
                            N/A
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {!allCheckoutItemsConfirmed && (
              <div className="mt-4 p-3 bg-red-100 border-2 border-red-400 rounded-lg">
                <p className="text-sm text-red-800 font-semibold">
                  ⚠️ <span className="font-bold">{Object.values(checkoutItems).filter(v => v).length}/{CHECKOUT_CHECKLIST.length}</span> items confirmed. Complete ALL items to enable signature.
                </p>
              </div>
            )}
            
            {allCheckoutItemsConfirmed && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700 font-medium flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  All customer care items confirmed! Please sign below.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Technician Signature - Only enabled after checkout complete */}
        <Card className={`mb-4 ${!allCheckoutItemsConfirmed ? 'opacity-50' : ''}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Technician Sign-Out
              {allCheckoutItemsConfirmed && <Check className="w-5 h-5 text-green-600" />}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-3">
              {allCheckoutItemsConfirmed 
                ? "By signing below, I confirm I have completed all customer care items and the inspection is accurate."
                : "Complete the Customer Care Checkout above to enable signature."
              }
            </p>
            <div className={`border-2 border-dashed rounded-lg bg-white relative ${!allCheckoutItemsConfirmed ? 'border-gray-200' : 'border-gray-300'}`}>
              <canvas
                ref={canvasRef}
                width={400}
                height={150}
                className={`w-full touch-none ${allCheckoutItemsConfirmed ? 'cursor-crosshair' : 'cursor-not-allowed'}`}
                onMouseDown={allCheckoutItemsConfirmed ? startDrawing : undefined}
                onMouseMove={allCheckoutItemsConfirmed ? draw : undefined}
                onMouseUp={allCheckoutItemsConfirmed ? stopDrawing : undefined}
                onMouseLeave={allCheckoutItemsConfirmed ? stopDrawing : undefined}
                onTouchStart={allCheckoutItemsConfirmed ? startDrawing : undefined}
                onTouchMove={allCheckoutItemsConfirmed ? draw : undefined}
                onTouchEnd={allCheckoutItemsConfirmed ? stopDrawing : undefined}
                data-testid="signature-canvas"
              />
              {!signature && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="text-gray-400 text-sm">
                    {allCheckoutItemsConfirmed ? "Sign here" : "Complete checkout first"}
                  </span>
                </div>
              )}
            </div>
            {signature && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={clearSignature}
                className="mt-2"
                data-testid="clear-signature-btn"
              >
                Clear Signature
              </Button>
            )}
          </CardContent>
        </Card>

        {error && (
          <div className="p-3 mb-4 bg-red-100 text-red-700 rounded flex items-center gap-2" data-testid="submit-error">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id="confirm"
                checked={confirmed}
                onCheckedChange={setConfirmed}
                disabled={!allCheckoutItemsConfirmed || !signature}
                data-testid="confirm-checkbox"
              />
              <label htmlFor="confirm" className="text-sm">
                I confirm that all inspection items have been accurately recorded, all customer care items have been completed, and the information provided is correct. I understand that once submitted, this inspection cannot be edited.
              </label>
            </div>
          </CardContent>
        </Card>

        <Button 
          className="w-full" 
          disabled={!confirmed || !signature || !allCheckoutItemsConfirmed || submitting}
          onClick={handleFinalSubmit}
          data-testid="final-submit-btn"
        >
          {submitting ? "Submitting..." : "Submit Inspection"}
        </Button>
      </div>
    </div>
  );
};

export default SubmitInspectionPage;
